import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { API_BASE } from "../lib/api";
import { toast } from "sonner";
import { LogOut, Download, Trash2, Plus, Upload, Tag, BarChart3, Users, BookOpen, FolderLock, Inbox } from "lucide-react";

const TABS = [
  { id: "overview", label: "Overview", icon: BarChart3 },
  { id: "subscribers", label: "Subscribers", icon: Users },
  { id: "editions", label: "Editions", icon: BookOpen },
  { id: "garage", label: "The Garage", icon: FolderLock },
  { id: "contacts", label: "Inquiries", icon: Inbox },
];

const authHdr = () => ({ Authorization: `Bearer ${localStorage.getItem("gb_admin_token")}` });

export default function AdminDashboard() {
  const [tab, setTab] = useState("overview");
  const nav = useNavigate();

  useEffect(() => {
    api.get("/admin/me").catch(() => nav("/admin/login"));
  }, [nav]);

  const logout = () => {
    localStorage.removeItem("gb_admin_token");
    nav("/admin/login");
  };

  return (
    <main data-testid="admin-dashboard" className="bg-black min-h-screen text-white">
      <div className="flex">
        <aside className="w-64 min-h-screen border-r border-white/5 p-6 sticky top-0">
          <p className="font-display text-2xl text-white">Good Bandit</p>
          <p className="kbd text-white/40">STUDIO · CONTROL ROOM</p>
          <nav className="mt-10 flex flex-col gap-1">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  data-testid={`admin-tab-${t.id}`}
                  onClick={() => setTab(t.id)}
                  className={`flex items-center gap-3 px-3 py-3 text-sm transition-colors ${active ? "bg-[var(--gb-gold)] text-black" : "text-white/70 hover:text-white hover:bg-white/5"}`}
                >
                  <Icon size={15} /> {t.label}
                </button>
              );
            })}
          </nav>
          <button onClick={logout} data-testid="admin-logout" className="mt-10 flex items-center gap-2 text-xs text-white/50 hover:text-white">
            <LogOut size={13} /> Sign out
          </button>
        </aside>

        <section className="flex-1 p-10 max-w-[1500px]">
          {tab === "overview" && <Overview />}
          {tab === "subscribers" && <Subscribers />}
          {tab === "editions" && <Editions />}
          {tab === "garage" && <GarageAdmin />}
          {tab === "contacts" && <Contacts />}
        </section>
      </div>
    </main>
  );
}

function Stat({ label, value, suffix }) {
  return (
    <div className="border border-white/10 p-6">
      <p className="kbd text-white/40">{label}</p>
      <p className="font-display text-5xl text-white mt-3">{value}{suffix || ""}</p>
    </div>
  );
}

function Overview() {
  const [d, setD] = useState(null);
  const [mag, setMag] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = () => {
    api.get("/admin/analytics").then(r => setD(r.data));
    api.get("/magazine/latest").then(r => setMag(r.data)).catch(() => setMag({ available: false }));
  };
  useEffect(load, []);

  const uploadPdf = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/magazine/pdf", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Attached to "${data.edition_title}"`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  if (!d) return <p className="text-white/40">Loading…</p>;
  return (
    <div data-testid="admin-overview">
      <p className="overline mb-2">Dashboard</p>
      <h1 className="font-display text-5xl text-white">Overview</h1>

      {/* One-click Issue PDF upload */}
      <div className="border border-[var(--gb-gold)]/30 bg-[var(--gb-gold)]/[0.03] p-6 mt-8 grid grid-cols-1 md:grid-cols-12 gap-6 items-center" data-testid="admin-quick-pdf-upload">
        <div className="md:col-span-7">
          <p className="overline mb-2">Quick Upload</p>
          <h3 className="font-display text-2xl text-white">Nocturne Issue PDF</h3>
          <p className="text-white/60 text-sm mt-2 leading-relaxed">
            Drop the latest issue's PDF here. It's automatically attached to the featured edition and served to every new subscriber.
          </p>
          {mag?.available ? (
            <p className="kbd text-[var(--gb-gold)] mt-3">LIVE ✓ · {mag.title}</p>
          ) : (
            <p className="kbd text-white/40 mt-3">NO PDF ATTACHED</p>
          )}
        </div>
        <div className="md:col-span-5 flex flex-col gap-3">
          <label className="gb-btn-primary justify-center cursor-pointer" data-testid="quick-pdf-label">
            {uploading ? "Uploading…" : "Upload Issue 01 PDF"}
            <input
              type="file"
              accept="application/pdf"
              className="hidden"
              data-testid="quick-pdf-input"
              onChange={(e) => uploadPdf(e.target.files?.[0])}
              disabled={uploading}
            />
          </label>
          {mag?.available && (
            <a
              href={`${API_BASE}/magazine/download`}
              target="_blank" rel="noopener noreferrer"
              className="gb-btn-outline justify-center text-[10px]"
              data-testid="quick-pdf-verify"
            >
              Verify download
            </a>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10">
        <Stat label="TOTAL SUBSCRIBERS" value={d.total_subscribers} />
        <Stat label="TODAY'S SIGNUPS" value={d.today_signups} />
        <Stat label="PAGE VIEWS" value={d.total_pageviews} />
        <Stat label="CONVERSION" value={d.conversion_rate_percent} suffix="%" />
        <Stat label="EDITIONS" value={d.total_editions} />
        <Stat label="INQUIRIES" value={d.total_contacts} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
        <div className="border border-white/10 p-6">
          <p className="overline mb-4">SUBSCRIBER SOURCES</p>
          {d.sources.map((s) => (
            <div key={s.source} className="flex justify-between items-center py-2 border-b border-white/5">
              <span className="text-sm text-white/80">{s.source}</span>
              <span className="font-mono-ed text-[var(--gb-gold)]">{s.count}</span>
            </div>
          ))}
          {d.sources.length === 0 && <p className="text-white/40 text-sm">No data yet.</p>}
        </div>
        <div className="border border-white/10 p-6">
          <p className="overline mb-4">14-DAY GROWTH</p>
          {d.growth.map((g) => (
            <div key={g.date} className="flex justify-between items-center py-2 border-b border-white/5">
              <span className="text-sm text-white/80 font-mono-ed">{g.date}</span>
              <span className="font-mono-ed text-[var(--gb-gold)]">{g.count}</span>
            </div>
          ))}
          {d.growth.length === 0 && <p className="text-white/40 text-sm">No signups yet.</p>}
        </div>
      </div>
    </div>
  );
}

function Subscribers() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");

  const load = async () => {
    const { data } = await api.get(`/admin/subscribers?q=${encodeURIComponent(q)}`);
    setItems(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const exportCsv = async () => {
    const res = await fetch(`${API_BASE}/admin/subscribers/export.csv`, { headers: authHdr() });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "good_bandit_subscribers.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const del = async (id) => {
    if (!window.confirm("Delete this subscriber?")) return;
    await api.delete(`/admin/subscribers/${id}`);
    load();
  };

  const updateTags = async (id, tagsStr) => {
    const tags = tagsStr.split(",").map(s => s.trim()).filter(Boolean);
    await api.patch(`/admin/subscribers/${id}/tags`, { tags });
    toast.success("Tags updated");
    load();
  };

  return (
    <div data-testid="admin-subscribers">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="overline mb-2">Audience</p>
          <h1 className="font-display text-5xl text-white">Subscribers</h1>
        </div>
        <div className="flex gap-3">
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search…" className="gb-input-dark w-64" data-testid="admin-search" />
          <button onClick={load} className="gb-btn-outline text-[10px]">Search</button>
          <button onClick={exportCsv} className="gb-btn-primary text-[10px]" data-testid="admin-export-csv"><Download size={12} /> Export CSV</button>
        </div>
      </div>

      <div className="border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-white/10 bg-white/5">
            <tr>
              {["NAME", "EMAIL", "BRAND", "SOURCE", "TAGS", "CONSENT", "DATE", ""].map(h => (
                <th key={h} className="kbd text-left px-4 py-3 text-white/50 font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.id} className="border-b border-white/5">
                <td className="px-4 py-3">{s.first_name}</td>
                <td className="px-4 py-3 text-[var(--gb-gold)]">{s.email}</td>
                <td className="px-4 py-3 text-white/70">{s.brand_or_dealership}</td>
                <td className="px-4 py-3 text-white/70">{s.persona || ""}</td>
                <td className="px-4 py-3 kbd text-white/60">{s.source_page}</td>
                <td className="px-4 py-3">
                  <input
                    defaultValue={(s.tags || []).join(", ")}
                    onBlur={(e) => updateTags(s.id, e.target.value)}
                    className="gb-input-dark text-xs"
                  />
                </td>
                <td className="px-4 py-3 kbd">{s.consent_status}</td>
                <td className="px-4 py-3 kbd text-white/40">{(s.subscribed_at || "").slice(0, 10)}</td>
                <td className="px-4 py-3"><button onClick={() => del(s.id)} className="text-white/40 hover:text-red-400"><Trash2 size={14} /></button></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={9} className="text-center py-12 text-white/40">No subscribers yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Editions() {
  const [items, setItems] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState(initEdition());

  function initEdition() {
    return { title: "", slug: "", release_date: "", summary: "", youtube_url: "", cover_image_url: "", hero_video_url: "", pdf_url: "", gallery_image_urls: [], is_published: true, is_featured: false };
  }

  const load = async () => setItems((await api.get("/admin/editions")).data);
  useEffect(() => { load(); }, []);

  const upload = async (file, kind) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);
    const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.url;
  };

  const save = async () => {
    if (!form.title || !form.slug) { toast.error("Title and slug required"); return; }
    try {
      await api.post("/admin/editions", form);
      toast.success("Edition created");
      setShow(false); setForm(initEdition()); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete edition?")) return;
    await api.delete(`/admin/editions/${id}`);
    load();
  };

  return (
    <div data-testid="admin-editions">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="overline mb-2">Magazine</p>
          <h1 className="font-display text-5xl text-white">Editions</h1>
        </div>
        <button onClick={() => { if (show) cancelEdit(); else { setEditingId(null); setForm(initEdition()); setShow(true); } }} className="gb-btn-primary text-[10px]" data-testid="admin-new-edition"><Plus size={12} /> {show ? "Cancel" : "New Edition"}</button>
      </div>

      {show && (
        <div className="border border-white/10 p-6 mb-8 grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="md:col-span-2 flex justify-between items-center">
            <p className="overline">{editingId ? "Editing edition" : "New edition"}</p>
            {editingId && <button onClick={cancelEdit} className="kbd text-white/40 hover:text-white">CANCEL</button>}
          </div>
          <div><label className="kbd">TITLE</label><input data-testid="edition-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="gb-input-dark mt-2" /></div>
          <div><label className="kbd">SLUG</label><input data-testid="edition-slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="gb-input-dark mt-2" placeholder="edition-02" /></div>
          <div><label className="kbd">RELEASE DATE</label><input value={form.release_date} onChange={(e) => setForm({ ...form, release_date: e.target.value })} className="gb-input-dark mt-2" placeholder="2026-03-15" /></div>
          <div><label className="kbd">YOUTUBE EMBED URL</label><input value={form.youtube_url} onChange={(e) => setForm({ ...form, youtube_url: e.target.value })} className="gb-input-dark mt-2" placeholder="https://www.youtube.com/embed/..." /></div>
          <div className="md:col-span-2"><label className="kbd">SUMMARY</label><textarea value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} className="gb-input-dark mt-2" rows={2} /></div>

          <UploadField label="COVER IMAGE" url={form.cover_image_url} onUpload={async (f) => setForm({ ...form, cover_image_url: await upload(f, "cover") })} />
          <UploadField label="HERO VIDEO (MP4)" url={form.hero_video_url} onUpload={async (f) => setForm({ ...form, hero_video_url: await upload(f, "hero") })} />
          <UploadField label="MAGAZINE PDF" url={form.pdf_url} onUpload={async (f) => setForm({ ...form, pdf_url: await upload(f, "pdf") })} />
          <div>
            <label className="kbd">GALLERY IMAGES (multiple)</label>
            <input type="file" multiple accept="image/*" data-testid="edition-gallery-input" onChange={async (e) => {
              const urls = [];
              for (const f of Array.from(e.target.files || [])) urls.push(await upload(f, "gallery"));
              setForm({ ...form, gallery_image_urls: [...form.gallery_image_urls, ...urls] });
              toast.success(`${urls.length} images uploaded`);
            }} className="block text-xs text-white/60 mt-2" />
            <p className="kbd text-white/30 mt-1">{form.gallery_image_urls.length} ATTACHED</p>
          </div>
          <div className="md:col-span-2 flex gap-6 items-center">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_published} onChange={(e) => setForm({ ...form, is_published: e.target.checked })} /> Published</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_featured} onChange={(e) => setForm({ ...form, is_featured: e.target.checked })} /> Featured</label>
            <button onClick={save} data-testid="edition-save" className="gb-btn-primary ml-auto">{editingId ? "Save changes" : "Save Edition"}</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {items.map((e) => (
          <div key={e.id} className="border border-white/10 p-5">
            {e.cover_image_url && <img src={e.cover_image_url.startsWith("http") ? e.cover_image_url : API_BASE.replace("/api","") + e.cover_image_url} className="w-full aspect-[3/4] object-cover mb-4" alt="" />}
            <p className="kbd text-white/40">{e.release_date}</p>
            <h3 className="font-display text-2xl text-white mt-2">{e.title}</h3>
            <p className="text-sm text-white/50 mt-2 line-clamp-2">{e.summary}</p>
            <div className="flex gap-2 mt-4 kbd">
              {e.is_featured && <span className="text-[var(--gb-gold)]">FEATURED</span>}
              {e.is_published ? <span className="text-white/40">PUBLISHED</span> : <span className="text-white/40">DRAFT</span>}
              {e.pdf_url ? <span className="text-white/40">PDF ✓</span> : <span className="text-white/40">NO PDF</span>}
            </div>
            <div className="flex gap-3 mt-4">
              <button onClick={() => openEdit(e)} data-testid={`edit-${e.id}`} className="gb-btn-outline text-[10px]">Edit</button>
              {e.pdf_url && (
                <button
                  data-testid={`announce-${e.id}`}
                  onClick={async () => {
                    if (!window.confirm(`Email every consented subscriber about "${e.title}"?`)) return;
                    try {
                      const { data } = await api.post(`/admin/editions/${e.id}/announce`);
                      toast.success(`Queued ${data.queued} subscribers`);
                    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
                  }}
                  className="gb-btn-outline text-[10px]"
                >Announce to subscribers</button>
              )}
              <button onClick={() => del(e.id)} className="text-white/40 hover:text-red-400 text-xs"><Trash2 size={12} className="inline mr-1" /> Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function UploadField({ label, url, onUpload }) {
  return (
    <div>
      <label className="kbd">{label}</label>
      <input type="file" onChange={(e) => e.target.files?.[0] && onUpload(e.target.files[0])} className="block text-xs text-white/60 mt-2" />
      {url && <p className="kbd text-[var(--gb-gold)] mt-1 truncate">UPLOADED ✓</p>}
    </div>
  );
}

function GarageAdmin() {
  const [folders, setFolders] = useState([]);
  const [form, setForm] = useState({ name: "", client_name: "", access_code: "", description: "" });
  const load = async () => setFolders((await api.get("/admin/folders")).data);
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name || !form.access_code) { toast.error("Name & access code required"); return; }
    try {
      await api.post("/admin/folders", form);
      toast.success("Folder created");
      setForm({ name: "", client_name: "", access_code: "", description: "" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const upload = async (folderId, file, label, category) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("label", label);
    fd.append("category", category);
    await api.post(`/admin/folders/${folderId}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    toast.success("File added");
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete folder?")) return;
    await api.delete(`/admin/folders/${id}`);
    load();
  };

  return (
    <div data-testid="admin-garage">
      <p className="overline mb-2">Portal</p>
      <h1 className="font-display text-5xl text-white">The Garage</h1>

      <div className="border border-white/10 p-6 mt-8 grid grid-cols-1 md:grid-cols-4 gap-4">
        <input placeholder="Folder name" data-testid="garage-folder-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="gb-input-dark" />
        <input placeholder="Client name" data-testid="garage-client-name" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} className="gb-input-dark" />
        <input placeholder="Access code" data-testid="garage-access-code-new" value={form.access_code} onChange={(e) => setForm({ ...form, access_code: e.target.value })} className="gb-input-dark font-mono-ed" />
        <button onClick={create} data-testid="garage-create-folder" className="gb-btn-primary text-[10px]"><Plus size={12} /> Create</button>
      </div>

      <div className="space-y-4 mt-8">
        {folders.map((f) => (
          <div key={f.id} className="border border-white/10 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-display text-2xl text-white">{f.name}</p>
                <p className="kbd text-white/40">{f.client_name} · CODE: <span className="text-[var(--gb-gold)]">{f.access_code}</span></p>
              </div>
              <button onClick={() => del(f.id)} className="text-white/40 hover:text-red-400 text-xs"><Trash2 size={12} className="inline" /></button>
            </div>
            <div className="mt-4">
              <p className="kbd mb-2">FILES ({(f.files || []).length})</p>
              {(f.files || []).map((file) => (
                <div key={file.id} className="text-sm text-white/70 flex justify-between py-1 border-b border-white/5">
                  <span>{file.label}</span><span className="kbd">{file.category}</span>
                </div>
              ))}
              <UploadInline folderId={f.id} onUpload={upload} />
            </div>
          </div>
        ))}
        {folders.length === 0 && <p className="text-white/40">No client folders yet.</p>}
      </div>
    </div>
  );
}

function UploadInline({ folderId, onUpload }) {
  const [label, setLabel] = useState("");
  const [category, setCategory] = useState("deliverable");
  const [file, setFile] = useState(null);
  return (
    <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mt-4">
      <input placeholder="Label" value={label} onChange={(e) => setLabel(e.target.value)} className="gb-input-dark text-xs" />
      <select value={category} onChange={(e) => setCategory(e.target.value)} className="gb-input-dark text-xs">
        {["preview", "shot_list", "draft_edit", "final_edit", "deliverable", "image"].map(c => <option key={c}>{c}</option>)}
      </select>
      <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-xs text-white/60 col-span-2" />
      <button onClick={() => file && onUpload(folderId, file, label || file.name, category)} className="gb-btn-outline text-[10px]"><Upload size={12} /> Add file</button>
    </div>
  );
}

function Contacts() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/contacts").then(r => setItems(r.data)); }, []);
  return (
    <div data-testid="admin-contacts">
      <p className="overline mb-2">Studio</p>
      <h1 className="font-display text-5xl text-white">Inquiries</h1>
      <div className="space-y-3 mt-10">
        {items.map((c) => (
          <div key={c.id} className="border border-white/10 p-5">
            <div className="flex justify-between">
              <p className="text-white font-medium">{c.name}<span className="text-white/40 ml-2">· {c.email}</span></p>
              <p className="kbd text-white/40">{(c.created_at || "").slice(0, 10)}</p>
            </div>
            <p className="kbd text-[var(--gb-gold)] mt-2">{c.project_type} · {c.budget_range}</p>
            {c.brand_dealership && <p className="text-sm text-white/60 mt-1">{c.brand_dealership}</p>}
            <p className="text-sm text-white/80 mt-3 leading-relaxed">{c.message}</p>
          </div>
        ))}
        {items.length === 0 && <p className="text-white/40">No inquiries yet.</p>}
      </div>
    </div>
  );
}
